import React, { useState } from "react";
import { FaEnvelope, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import petrollLogo from "../assets/images/Petroll Logo small.png";
import Cookies from "js-cookie";
import Footer from "../layout/Footer";

const API_URL = process.env.REACT_APP_API_URL;

const Login = () => {
    const [userEmail, setUserEmail] = useState("");
    const [password, setPassword] = useState("");
    const [message, setMessage] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    const validateEmail = (email) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage("");

        if (!userEmail || !password) {
            setMessage("All fields are required");
            return;
        }

        if (!validateEmail(userEmail)) {
            setMessage("Please enter a valid email");
            return;
        }

        const user = { userEmail, password };
        setIsSubmitting(true);

        try {
            const response = await fetch(`${API_URL}/login/admin`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(user),
            });

            const data = await response.json();

            if (!response.ok) {
                setMessage(data.errorMessage || "Invalid login credentials");
                setIsSubmitting(false);
                return;
            }
            if (data.user.userType.toLowerCase() !== "admin") {
                setMessage("Invalid login credentials");
                setIsSubmitting(false);
                return;
            }

            Cookies.set("token", data.token, { expires: 6 / 24 });
            navigate("/pets");
        } catch (error) {
            setMessage("Something went wrong. Try again!");
            console.error(error);
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div className="ad-page">
                <div className="ad-panel ad-panel--console">
                    <div className="ad-console-top">
                        <img src={petrollLogo} alt="Petroll" className="ad-logo" />
                        <span className="ad-console-tag">ADMIN</span>
                    </div>

                    <div className="ad-console-body">
                        <h1 className="ad-headline">Operator access</h1>
                        <p className="ad-subline">
                            This console controls live records for every animal,
                            booking and staff account on the platform. Access is
                            restricted and logged.
                        </p>
                    </div>

                    <div className="ad-readout">
                        <div className="ad-readout-row">
                            <span className="ad-dot" aria-hidden="true" />
                            <span>STATUS&nbsp;&nbsp;OPERATIONAL</span>
                        </div>
                        <div className="ad-readout-row">
                            <span>ENV&nbsp;&nbsp;production</span>
                        </div>
                        <div className="ad-readout-row">
                            <span>ACCESS&nbsp;&nbsp;role:admin required</span>
                        </div>
                    </div>
                </div>

                <div className="ad-panel ad-panel--form">
                    <img src={petrollLogo} alt="Petroll" className="ad-logo ad-logo--mobile" />

                    <div className="ad-form-wrap">
                        <span className="ad-eyebrow">Sign in</span>
                        <h2 className="ad-form-title">Admin login</h2>

                        {message && (
                            <div className="ad-alert" role="alert">
                                {message}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} noValidate>
                            <label className="ad-field">
                                <span className="ad-field-label">Email</span>
                                <span className="ad-input-shell">
                                    <FaEnvelope className="ad-input-icon" aria-hidden="true" />
                                    <input
                                        type="email"
                                        placeholder="you@petroll.com"
                                        value={userEmail}
                                        onChange={(e) => setUserEmail(e.target.value)}
                                        required
                                        className="ad-input"
                                        autoComplete="email"
                                    />
                                </span>
                            </label>

                            <label className="ad-field">
                                <span className="ad-field-label">Password</span>
                                <span className="ad-input-shell">
                                    <FaLock className="ad-input-icon" aria-hidden="true" />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        placeholder="Enter your password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="ad-input ad-input--password"
                                        autoComplete="current-password"
                                    />
                                    <button
                                        type="button"
                                        className="ad-eye-toggle"
                                        onClick={() => setShowPassword((v) => !v)}
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                    >
                                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                                    </button>
                                </span>
                            </label>

                            <button type="submit" className="ad-submit" disabled={isSubmitting}>
                                {isSubmitting ? "Verifying…" : "Log in"}
                            </button>
                        </form>

                        <p className="ad-help-text">
                            Forgot your password? Contact your system administrator.
                        </p>
                    </div>
                </div>
            </div>
            <Footer />

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=IBM+Plex+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap');

                :root {
                    --ad-ink-950: #0D1210;
                    --ad-panel-900: #121B18;
                    --ad-line-700: #253530;
                    --ad-paper-50: #F6F7F5;
                    --ad-paper-100: #EBEDE9;
                    --ad-text-900: #171D1A;
                    --ad-text-500: #5C6862;
                    --ad-amber-500: #D98B2B;
                    --ad-amber-600: #C1791F;
                    --ad-green-400: #4FAE8C;
                    --ad-danger-bg: #FBEAE5;
                    --ad-danger-text: #B14A2C;
                }

                .ad-page {
                    min-height: 100vh;
                    display: flex;
                    font-family: 'Inter', sans-serif;
                    color: var(--ad-text-900);
                }

                .ad-panel {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    padding: 44px 52px;
                    position: relative;
                }

                .ad-panel--console {
                    background-color: var(--ad-panel-900);
                    background-image:
                        linear-gradient(var(--ad-line-700) 1px, transparent 1px),
                        linear-gradient(90deg, var(--ad-line-700) 1px, transparent 1px);
                    background-size: 34px 34px;
                    background-position: -1px -1px;
                    color: #E7ECE9;
                    justify-content: space-between;
                }

                .ad-console-top {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .ad-logo {
                    height: 26px;
                    object-fit: contain;
                }

                .ad-logo--mobile {
                    display: none;
                    height: 24px;
                    margin: 0 auto 28px;
                }

                .ad-console-tag {
                    font-family: 'IBM Plex Mono', monospace;
                    font-size: 11px;
                    letter-spacing: 0.16em;
                    color: var(--ad-amber-500);
                    border: 1px solid var(--ad-line-700);
                    padding: 3px 8px;
                    border-radius: 4px;
                }

                .ad-console-body {
                    max-width: 400px;
                }

                .ad-headline {
                    font-family: 'Space Grotesk', sans-serif;
                    font-weight: 600;
                    font-size: 38px;
                    line-height: 1.15;
                    margin: 0 0 16px;
                    color: #FFFFFF;
                }

                .ad-subline {
                    font-size: 14.5px;
                    line-height: 1.65;
                    color: rgba(231, 236, 233, 0.62);
                    margin: 0;
                }

                .ad-readout {
                    font-family: 'IBM Plex Mono', monospace;
                    font-size: 12px;
                    color: rgba(231, 236, 233, 0.68);
                    border-top: 1px solid var(--ad-line-700);
                    padding-top: 18px;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    letter-spacing: 0.02em;
                }

                .ad-readout-row {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .ad-dot {
                    width: 7px;
                    height: 7px;
                    border-radius: 50%;
                    background: var(--ad-green-400);
                    box-shadow: 0 0 0 0 rgba(79, 174, 140, 0.6);
                    animation: ad-pulse 2.4s ease-out infinite;
                }

                @keyframes ad-pulse {
                    0%   { box-shadow: 0 0 0 0 rgba(79, 174, 140, 0.5); }
                    70%  { box-shadow: 0 0 0 6px rgba(79, 174, 140, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(79, 174, 140, 0); }
                }

                .ad-panel--form {
                    background: var(--ad-paper-50);
                    align-items: center;
                    justify-content: center;
                }

                .ad-form-wrap {
                    width: 100%;
                    max-width: 350px;
                }

                .ad-eyebrow {
                    display: block;
                    font-family: 'IBM Plex Mono', monospace;
                    font-size: 11px;
                    letter-spacing: 0.14em;
                    text-transform: uppercase;
                    color: var(--ad-text-500);
                    margin-bottom: 10px;
                }

                .ad-form-title {
                    font-family: 'Space Grotesk', sans-serif;
                    font-weight: 600;
                    font-size: 27px;
                    margin: 0 0 26px;
                    color: var(--ad-text-900);
                }

                .ad-alert {
                    background: var(--ad-danger-bg);
                    color: var(--ad-danger-text);
                    font-size: 13.5px;
                    font-weight: 500;
                    padding: 11px 14px;
                    border-radius: 8px;
                    margin-bottom: 20px;
                }

                .ad-field {
                    display: block;
                    margin-bottom: 18px;
                }

                .ad-field-label {
                    display: block;
                    font-family: 'IBM Plex Mono', monospace;
                    font-size: 11.5px;
                    letter-spacing: 0.06em;
                    text-transform: uppercase;
                    color: var(--ad-text-500);
                    margin-bottom: 7px;
                }

                .ad-input-shell {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                .ad-input-icon {
                    position: absolute;
                    left: 14px;
                    color: #9AA6A0;
                    font-size: 13px;
                    pointer-events: none;
                }

                .ad-input {
                    width: 100%;
                    height: 46px;
                    padding: 0 14px 0 40px;
                    border-radius: 8px;
                    border: 1.5px solid var(--ad-paper-100);
                    background: #FFFFFF;
                    font-size: 14px;
                    font-family: 'Inter', sans-serif;
                    color: var(--ad-text-900);
                    outline: none;
                    transition: border-color 0.15s ease, box-shadow 0.15s ease;
                    box-sizing: border-box;
                }

                .ad-input::placeholder {
                    color: #AEB6B1;
                }

                .ad-input:focus {
                    border-color: var(--ad-amber-500);
                    box-shadow: 0 0 0 3.5px rgba(217, 139, 43, 0.14);
                }

                .ad-input--password {
                    padding-right: 42px;
                }

                .ad-eye-toggle {
                    position: absolute;
                    right: 13px;
                    background: none;
                    border: none;
                    padding: 4px;
                    cursor: pointer;
                    color: #8B968F;
                    display: flex;
                    align-items: center;
                    transition: color 0.15s ease;
                }

                .ad-eye-toggle:hover {
                    color: var(--ad-text-900);
                }

                .ad-submit {
                    width: 100%;
                    height: 47px;
                    margin-top: 8px;
                    border: none;
                    border-radius: 8px;
                    background: var(--ad-ink-950);
                    color: #FFFFFF;
                    font-family: 'Inter', sans-serif;
                    font-weight: 600;
                    font-size: 14.5px;
                    cursor: pointer;
                    transition: background 0.15s ease;
                }

                .ad-submit:hover:not(:disabled) {
                    background: #000000;
                }

                .ad-submit:disabled {
                    opacity: 0.65;
                    cursor: not-allowed;
                }

                .ad-help-text {
                    text-align: center;
                    font-size: 12.5px;
                    color: var(--ad-text-500);
                    margin-top: 22px;
                }

                @media (prefers-reduced-motion: reduce) {
                    .ad-dot { animation: none; }
                }

                @media (max-width: 900px) {
                    .ad-page { flex-direction: column; }
                    .ad-panel--console { padding: 32px 28px 40px; min-height: 240px; }
                    .ad-headline { font-size: 28px; }
                    .ad-readout { margin-top: 24px; }
                    .ad-panel--form { padding: 40px 26px 56px; }
                    .ad-logo--mobile { display: block; }
                }
            `}</style>
        </>
    );
};

export default Login;